import React, { useState, useEffect, useCallback } from 'react';
import { perksAPI } from '../services/api';

const GRADIENTS = {
  sunset: 'linear-gradient(150deg,#ff4d8d 0%,#ff7a00 58%,#facc15 100%)',
  violet: 'linear-gradient(150deg,#8b5cf6 0%,#ec4899 52%,#fb923c 100%)',
  ocean: 'linear-gradient(150deg,#0ea5e9 0%,#22c55e 52%,#facc15 100%)',
  indigo: 'linear-gradient(150deg,#6366f1 0%,#22d3ee 100%)',
  emerald: 'linear-gradient(150deg,#10b981 0%,#0ea5e9 100%)',
  slate: 'linear-gradient(150deg,#334155 0%,#64748b 100%)',
};

const EMPTY = {
  title: '', partner_name: '', category: 'Perks', gradient: 'sunset', value_label: '',
  card_summary: '', description: '', who_its_for: '', redemption_type: 'link',
  redemption_code: '', redemption_url: '', redemption_instructions: '', status: 'active', sort_order: 0,
};

const inp = { width: '100%', padding: '9px 11px', border: '1px solid #e4e4e7', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
const label = { fontSize: 12, fontWeight: 600, color: '#52525b', display: 'block', marginBottom: 5 };
const btn = (bg, fg) => ({ padding: '8px 14px', borderRadius: 8, border: 'none', background: bg, color: fg, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' });

function Thumb({ gradient, size = 34 }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, borderRadius: 8, overflow: 'hidden', flex: '0 0 auto', border: '1px solid #eee' }}>
      <div style={{ position: 'absolute', inset: 0, background: GRADIENTS[gradient] || GRADIENTS.sunset }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 28% 20%, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.5) 9%, transparent 18%)' }} />
    </div>
  );
}

export default function Perks() {
  const [perks, setPerks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('perks');
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState(null); // null = closed; object = editing/new
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await perksAPI.getPerks();
      setPerks(r.data?.data?.perks || []);
    } catch (e) { setErr('Failed to load perks'); }
    setLoading(false);
  }, []);

  const loadRedemptions = useCallback(async () => {
    try {
      const r = await perksAPI.getRedemptions();
      setRedemptions(r.data?.data?.redemptions || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'redemptions') loadRedemptions(); }, [tab, loadRedemptions]);

  const openNew = () => { setErr(''); setForm({ ...EMPTY }); };
  const openEdit = (p) => { setErr(''); setForm({ ...EMPTY, ...p }); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { setErr('Title is required'); return; }
    setSaving(true); setErr('');
    try {
      if (form.id) await perksAPI.updatePerk(form.id, form);
      else await perksAPI.createPerk(form);
      setForm(null);
      await load();
    } catch (e) { setErr('Failed to save perk'); }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete perk "${p.title}"? This also removes its claims.`)) return;
    try { await perksAPI.deletePerk(p.id); await load(); } catch (e) { setErr('Failed to delete'); }
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Perks</h1>
        {tab === 'perks' && <button style={btn('#18181b', '#fff')} onClick={openNew}>+ Add perk</button>}
      </div>
      <p style={{ color: '#71717b', fontSize: 14, marginTop: 4 }}>
        Pro-only partner perks. Users with Compliance Pro (or an admin-granted Pro flag) can claim these in the app.
      </p>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0 20px' }}>
        {['perks', 'redemptions'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e4e4e7', background: tab === t ? '#18181b' : '#fff', color: tab === t ? '#fff' : '#52525b', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      {err && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '9px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      {tab === 'perks' ? (
        loading ? <div style={{ color: '#a1a1aa' }}>Loading…</div> : (
          <div style={{ border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#fafafa', textAlign: 'left', color: '#71717b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '10px 12px' }}>Perk</th>
                  <th style={{ padding: '10px 12px' }}>Category</th>
                  <th style={{ padding: '10px 12px' }}>Value</th>
                  <th style={{ padding: '10px 12px' }}>Redeem</th>
                  <th style={{ padding: '10px 12px' }}>Status</th>
                  <th style={{ padding: '10px 12px' }}>Claims</th>
                  <th style={{ padding: '10px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {perks.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 20, color: '#a1a1aa', textAlign: 'center' }}>No perks yet. Click “Add perk”.</td></tr>
                )}
                {perks.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid #f1f1f4' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Thumb gradient={p.gradient} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#18181b' }}>{p.title}</div>
                          {p.partner_name && <div style={{ fontSize: 12, color: '#a1a1aa' }}>{p.partner_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#52525b' }}>{p.category}</td>
                    <td style={{ padding: '10px 12px', color: '#52525b' }}>{p.value_label || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#52525b', textTransform: 'capitalize' }}>{p.redemption_type}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: p.status === 'active' ? '#ecfdf3' : '#f4f4f5', color: p.status === 'active' ? '#027a48' : '#71717b' }}>{p.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#52525b' }}>{p.claims || 0}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn('#f4f4f5', '#18181b'), padding: '6px 11px', marginRight: 6 }} onClick={() => openEdit(p)}>Edit</button>
                      <button style={{ ...btn('#fef2f2', '#b91c1c'), padding: '6px 11px' }} onClick={() => remove(p)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div style={{ border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#fafafa', textAlign: 'left', color: '#71717b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '10px 12px' }}>Perk</th>
                <th style={{ padding: '10px 12px' }}>User</th>
                <th style={{ padding: '10px 12px' }}>Email</th>
                <th style={{ padding: '10px 12px' }}>Claimed</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && <tr><td colSpan={4} style={{ padding: 20, color: '#a1a1aa', textAlign: 'center' }}>No claims yet.</td></tr>}
              {redemptions.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f1f1f4' }}>
                  <td style={{ padding: '10px 12px', color: '#18181b', fontWeight: 500 }}>{r.perk_title}</td>
                  <td style={{ padding: '10px 12px', color: '#52525b' }}>{r.user_name || '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#52525b' }}>{r.user_email || '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#71717b' }}>{r.claimed_at ? new Date(r.claimed_at).toLocaleString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }} onClick={() => !saving && setForm(null)}>
          <div style={{ width: 520, maxWidth: '100%', height: '100%', background: '#fff', overflowY: 'auto', padding: 24, boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{form.id ? 'Edit perk' : 'New perk'}</h2>
              <button style={{ ...btn('#f4f4f5', '#52525b'), padding: '6px 10px' }} onClick={() => setForm(null)}>Close</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><span style={label}>Title *</span><input style={inp} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. $5,000 in AWS credits" /></div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}><span style={label}>Partner</span><input style={inp} value={form.partner_name || ''} onChange={(e) => set('partner_name', e.target.value)} placeholder="AWS" /></div>
                <div style={{ flex: 1 }}><span style={label}>Category</span><input style={inp} value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Cloud & Infra" /></div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}><span style={label}>Value label</span><input style={inp} value={form.value_label || ''} onChange={(e) => set('value_label', e.target.value)} placeholder="$5,000 in credits" /></div>
                <div style={{ flex: 1 }}><span style={label}>Sort order</span><input style={inp} type="number" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} /></div>
              </div>

              <div>
                <span style={label}>Thumbnail gradient</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.keys(GRADIENTS).map((g) => (
                    <button key={g} type="button" onClick={() => set('gradient', g)}
                      style={{ width: 44, height: 34, borderRadius: 8, cursor: 'pointer', background: GRADIENTS[g], border: form.gradient === g ? '2px solid #18181b' : '1px solid #e4e4e7' }} title={g} />
                  ))}
                </div>
              </div>

              <div><span style={label}>Card summary</span><input style={inp} value={form.card_summary || ''} onChange={(e) => set('card_summary', e.target.value)} placeholder="Short blurb shown on the card" /></div>
              <div><span style={label}>Who it's for</span><input style={inp} value={form.who_its_for || ''} onChange={(e) => set('who_its_for', e.target.value)} placeholder="Early-stage startups on AWS" /></div>
              <div><span style={label}>Description</span><textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={form.description || ''} onChange={(e) => set('description', e.target.value)} placeholder="Full details shown in the drawer" /></div>

              <div style={{ borderTop: '1px solid #eee', paddingTop: 14 }}>
                <span style={label}>Redemption</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {['link', 'code'].map((t) => (
                    <button key={t} type="button" onClick={() => set('redemption_type', t)}
                      style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e4e4e7', background: form.redemption_type === t ? '#18181b' : '#fff', color: form.redemption_type === t ? '#fff' : '#52525b', fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                      {t}
                    </button>
                  ))}
                </div>
                {form.redemption_type === 'code'
                  ? <div style={{ marginBottom: 10 }}><span style={label}>Code</span><input style={inp} value={form.redemption_code || ''} onChange={(e) => set('redemption_code', e.target.value)} placeholder="NAYTIVE50" /></div>
                  : null}
                <div style={{ marginBottom: 10 }}><span style={label}>Link {form.redemption_type === 'link' ? '(shown to user)' : '(optional)'}</span><input style={inp} value={form.redemption_url || ''} onChange={(e) => set('redemption_url', e.target.value)} placeholder="https://partner.com/naytive" /></div>
                <div><span style={label}>Instructions (optional)</span><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.redemption_instructions || ''} onChange={(e) => set('redemption_instructions', e.target.value)} placeholder="How to apply the code / any steps" /></div>
              </div>

              <div><span style={label}>Status</span>
                <select style={inp} value={form.status} onChange={(e) => set('status', e.target.value)}>
                  <option value="active">Active (visible to users)</option>
                  <option value="draft">Draft (hidden)</option>
                </select>
              </div>

              {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button style={{ ...btn('#18181b', '#fff'), opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving…' : (form.id ? 'Save changes' : 'Create perk')}</button>
                <button style={btn('#f4f4f5', '#52525b')} onClick={() => setForm(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
